import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarClock } from 'lucide-react';
import { paymentExpectationOf, realisationDueDate, type PaymentExpectationKind } from '@fint/validation';
import { api, ApiError } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';
import { formatDate } from '@/lib/dates';
import { Badge, Button, Card, CardContent, Input, Sheet, SheetBody, SheetFooter, SheetForm, SheetHeader, Textarea } from '@/features/common/ui';

/**
 * When the money on an issued invoice is really expected (0039) - Zoho Books'
 * "Expected Payment Date", with its "don't remind until then".
 *
 * ── WHY NOT VOID ──
 * A customer who will pay late has not cancelled the sale. Voiding takes the
 * invoice off a return that may already be filed; writing off gives the debt
 * up. This changes only what the forecast counts on and whether the customer
 * is reminded. The invoice, what is owed, the books and the return stay as
 * they are - which is why it is allowed in a filed period.
 *
 * ── THE EXPORT DEADLINE ──
 * Pausing the customer's reminders never pauses the business's own warning
 * that an unpaid export is running out of FEMA time and its zero-rating with
 * it; the panel says so, with the date.
 */

export interface ExpectationFields {
  id: string;
  number: string;
  status: string;
  date: string;
  currency: string;
  balanceDue: string;
  expectedPaymentOn?: string | null;
  paymentNotExpected?: number | boolean | null;
  paymentExpectationNote?: string | null;
  remindersPaused?: number | boolean | null;
  customer: { gstTreatment?: string | null } | null;
}

export function PaymentExpectation({ invoice, canEdit }: { invoice: ExpectationFields; canEdit: boolean }) {
  const [editing, setEditing] = useState(false);
  // `status` is derived: sent, overdue or part-paid are all still owed.
  if (['draft', 'void', 'written_off', 'paid'].includes(invoice.status) || Number(invoice.balanceDue) <= 0) return null;
  const kind = paymentExpectationOf({ expectedPaymentOn: invoice.expectedPaymentOn ?? null, paymentNotExpected: invoice.paymentNotExpected ?? 0 });
  const paused = !!invoice.remindersPaused;
  const isExport = invoice.customer?.gstTreatment === 'overseas';
  const femaDue = isExport ? realisationDueDate(invoice.date, invoice.currency === 'INR') : null;

  const headline =
    kind === 'expected'
      ? `Expected ${formatDate(invoice.expectedPaymentOn)}`
      : kind === 'not_expected'
        ? 'Not expected soon'
        : 'Expected on its terms';
  const detail =
    kind === 'expected'
      ? `Still owed. Dashboards stop counting it as overdue and expect it on that date${paused ? '; no payment reminders until then' : ''}.`
      : kind === 'not_expected'
        ? `Still owed, but not counted as overdue or as money coming in${paused ? '; payment reminders are paused' : ''}.`
        : 'Counted from its due date. A customer who has told you they will pay late? Say when, instead of voiding it.';

  return (
    <div className="print:hidden" data-testid="payment-expectation">
      <Card>
        <CardContent className="flex flex-wrap items-start justify-between gap-3 pt-4">
          <div className="min-w-0 space-y-1">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Payment</p>
            <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
              {headline}
              {kind !== 'on_terms' && paused ? <Badge variant="neutral">Reminders paused</Badge> : null}
              {kind === 'not_expected' ? <Badge variant="warning">Not in forecast</Badge> : null}
            </p>
            <p className="text-sm text-muted-foreground">{detail}</p>
            {kind !== 'on_terms' && invoice.paymentExpectationNote ? (
              <p className="text-sm italic text-muted-foreground">“{invoice.paymentExpectationNote}”</p>
            ) : null}
            {femaDue ? (
              <p className="text-xs text-muted-foreground" data-testid="payment-expectation-fema">
                An export: the money has to arrive by {formatDate(femaDue)} (FEMA) or the export loses its zero-rating. Fint warns you
                as that date nears, whatever is set here.
              </p>
            ) : null}
          </div>
          {canEdit ? (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="payment-expectation-edit">
              <CalendarClock className="size-4" aria-hidden />
              {kind === 'on_terms' ? 'Paying late?' : 'Change'}
            </Button>
          ) : null}
        </CardContent>
      </Card>
      {editing ? <ExpectationSheet invoice={invoice} kind={kind} onClose={() => setEditing(false)} /> : null}
    </div>
  );
}

const CHOICES: Array<{ value: PaymentExpectationKind; label: string; hint: string }> = [
  { value: 'on_terms', label: 'On its terms', hint: 'As the due date says. The usual reminders run.' },
  { value: 'expected', label: 'Expected on a date', hint: 'The customer told you when. Not overdue until then; expected in that month.' },
  { value: 'not_expected', label: 'Not expected soon', hint: 'Still owed, but not money to plan on. Left out of what is expected.' },
];

function ExpectationSheet({ invoice, kind, onClose }: { invoice: ExpectationFields; kind: PaymentExpectationKind; onClose: () => void }) {
  useEscapeToClose(onClose);
  const org = useActiveOrg();
  const queryClient = useQueryClient();
  const [choice, setChoice] = useState<PaymentExpectationKind>(kind === 'on_terms' ? 'expected' : kind);
  const [date, setDate] = useState(invoice.expectedPaymentOn ?? '');
  const [note, setNote] = useState(invoice.paymentExpectationNote ?? '');
  const [pause, setPause] = useState(kind === 'on_terms' ? true : !!invoice.remindersPaused);

  const save = useMutation({
    mutationFn: () =>
      api.put(
        `/invoices/${invoice.id}/expectation`,
        choice === 'on_terms'
          ? { kind: 'on_terms' }
          : {
              kind: choice,
              ...(choice === 'expected' ? { date } : {}),
              ...(note.trim() ? { note: note.trim() } : {}),
              pauseReminders: pause,
            },
      ),
    onSuccess: () => {
      for (const key of ['invoice', 'invoices', 'cfo-summary', 'cfo-chase', 'dashboard']) void queryClient.invalidateQueries({ queryKey: [key, org?.id] });
      void queryClient.invalidateQueries({ queryKey: ['invoice', org?.id, invoice.id] });
      onClose();
    },
  });
  const error = save.error instanceof ApiError ? save.error : undefined;

  return (
    <Sheet label={`When ${invoice.number} will be paid`} onClose={onClose} testId="payment-expectation-sheet">
      <SheetHeader title={`When will ${invoice.number} be paid?`} onClose={onClose} />
      <SheetForm
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <SheetBody>
          <p className="text-sm text-muted-foreground">
            This changes what the dashboards expect and when reminders go. The invoice, what is owed, the books and the GST return stay as they are.
          </p>
          <fieldset className="space-y-2">
            <legend className="sr-only">When it will be paid</legend>
            {CHOICES.map((c) => (
              <label key={c.value} className="flex min-h-11 cursor-pointer items-start gap-3 rounded-md border border-border p-3 sm:min-h-0">
                <input
                  type="radio"
                  name="expectation"
                  value={c.value}
                  checked={choice === c.value}
                  onChange={() => setChoice(c.value)}
                  className="mt-0.5"
                  data-testid={`payment-expectation-${c.value}`}
                />
                <span>
                  <span className="block text-sm font-medium">{c.label}</span>
                  <span className="block text-xs text-muted-foreground">{c.hint}</span>
                </span>
              </label>
            ))}
          </fieldset>
          {choice === 'expected' ? (
            <Input
              label="Expected payment date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              error={error?.fieldError('date')}
              required
              data-testid="payment-expectation-date"
            />
          ) : null}
          {choice !== 'on_terms' ? (
            <>
              <Textarea label="Why (optional)" rows={2} maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What the customer said" />
              <label className="flex min-h-11 items-center gap-2 text-sm sm:min-h-0">
                <input type="checkbox" checked={pause} onChange={(e) => setPause(e.target.checked)} data-testid="payment-expectation-pause" />
                {choice === 'expected' ? "Don't remind the customer until then" : "Don't remind the customer"}
              </label>
            </>
          ) : null}
          {error && !error.fieldError('date') ? (
            <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error.message}
            </p>
          ) : null}
        </SheetBody>
        <SheetFooter className="justify-end">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={save.isPending} data-testid="payment-expectation-save">
            Save
          </Button>
        </SheetFooter>
      </SheetForm>
    </Sheet>
  );
}
